import { markRaw } from 'vue';
import { createStore } from 'vuex';
import RequestOnceAtATime from '@app/utilities/requestOnceAtATime';
import {
  Universe, EntityType, User,
} from '@app/models';
import type CustomRole from '@app/authorization/customRole';
import type BuiltInRole from '@app/authorization/builtInRole';
import { openWebSocket, closeWebSocket, subscribeToUniverse } from '@app/websocket';
import { getThemePreference, setThemePreference, ThemePreference } from '@app/utilities/theme';

interface UndoDeleteToast {
  componentName: 'UndoDeleteToast';
  message: string;
  undoDelete: () => any;
}

interface ErrorMessageToast {
  componentName: 'ErrorMessageToast';
  message: string;
}

interface SuccessMessageToast {
  componentName: 'SuccessMessageToast';
  message: string;
}

interface ExportProgressToast {
  componentName: 'ExportProgressToast';
  message: string;
}

type Toast = { key: string } & (
  UndoDeleteToast
  | ErrorMessageToast
  | SuccessMessageToast
  | ExportProgressToast
);

export type CollabStatus = 'connected' | 'reconnecting' | 'authError';

export interface CollabConnectionState {
  documentName: string;
  status: CollabStatus;
  hasUnsyncedChanges: boolean;
}

export interface CowState {
  loggedInUser?: User;
  actingAs: CustomRole | BuiltInRole | undefined;
  editing: boolean;
  selectedEntityTypeId?: string;
  selectedUniverse?: Universe;
  universes?: Array<Universe>;
  entityTypesByUniverseId: Record<string, Array<EntityType>>;
  toasts: Array<Toast>;
  heartbeatSender: RequestOnceAtATime | undefined;
  openModals: string[],
  websocket: WebSocket | undefined;
  collabConnections: Record<string, CollabConnectionState>;
  theme: ThemePreference;
}

function getEmptyState(): CowState {
  return {
    actingAs: undefined,
    editing: true,
    loggedInUser: undefined,
    selectedEntityTypeId: undefined,
    selectedUniverse: undefined,
    universes: undefined,
    entityTypesByUniverseId: {},
    toasts: [],
    heartbeatSender: undefined,
    openModals: [] as string[], // Array of open modal IDs, in order of opening
    websocket: undefined,
    collabConnections: {},
    theme: getThemePreference(),
  };
}

const store = createStore({
  state: {
    ...getEmptyState(),
  },
  mutations: {
    setSelectedUniverse(state: CowState, universe: Universe) {
      state.selectedUniverse = universe;
    },
    setLoggedInUser(state: CowState, loggedInUser: User) {
      if (state.loggedInUser !== loggedInUser) {
        state.loggedInUser = loggedInUser;

        if (state.heartbeatSender) {
          state.heartbeatSender.send();
        }

        state.websocket = markRaw(openWebSocket());
      }
    },
    setSelectedEntityTypeId(state: CowState, id: string) {
      state.selectedEntityTypeId = id;
    },
    setUniverses(state: CowState, universes: Array<Universe>) {
      state.universes = universes;
      if (state.websocket) {
        for (const universe of universes) {
          subscribeToUniverse(state.websocket, universe.id);
        }
      }
    },
    setHeartbeatSender(state: CowState, heartbeatSender: RequestOnceAtATime) {
      state.heartbeatSender = heartbeatSender;
    },
    addUniverse(state: CowState, universe: Universe) {
      if (state.universes !== undefined) {
        state.universes.push(universe);
        if (state.websocket) {
          subscribeToUniverse(state.websocket, universe.id);
        }
      }
    },
    removeUniverse(state: CowState, universe: Universe) {
      if (state.universes) {
        const index = state.universes.indexOf(universe);
        state.universes.splice(index, 1);
      }
    },
    pushAlertMessage(state: CowState, content: string) {
      state.toasts.push({
        key: content,
        componentName: 'ErrorMessageToast',
        message: content,
      });
    },
    pushSuccessMessage(state: CowState, content: string) {
      state.toasts.push({
        key: content,
        componentName: 'SuccessMessageToast',
        message: content,
      });
    },
    pushExportProgressToast(state: CowState, key: string) {
      state.toasts.push({
        key,
        componentName: 'ExportProgressToast',
        message: 'Preparing export\u2026 This may take a while.',
      });
    },
    removeToast(state: CowState, key: string) {
      const index = state.toasts.findIndex((t) => t.key === key);
      if (index !== -1) {
        state.toasts.splice(index, 1);
      }
    },
    setWebSocket(state: CowState, websocket: WebSocket) {
      state.websocket = markRaw(websocket);
    },
    clearState(state: CowState) {
      if (state.websocket) {
        closeWebSocket(state.websocket);
      }
      const newEmptyState = getEmptyState();
      Object.assign(state, newEmptyState);
    },
    toggleEditing(state: CowState) {
      state.editing = !state.editing;
    },
    setTheme(state: CowState, theme: ThemePreference) {
      state.theme = theme;
      setThemePreference(theme);
    },
    addOpenModal(state: CowState, modalId: string) {
      state.openModals.push(modalId);
    },
    removeOpenModal(state: CowState, modalId: string) {
      // NOTE: modal.vue may call this removeOpenModal extra times
      // thus, this operation must be idempotent.
      state.openModals = state.openModals.filter((id) => id !== modalId);
    },
    updateEntityType(state: CowState, updatedEntityType) {
      if (state.entityTypesByUniverseId[updatedEntityType.universeId]) {
        for (const [index, entityType] of state.entityTypesByUniverseId[updatedEntityType.universeId].entries()) {
          if (entityType.id === updatedEntityType.id) {
            Object.assign(
              state.entityTypesByUniverseId[updatedEntityType.universeId][index],
              updatedEntityType,
            );
          }
        }
      }
    },
    registerCollabConnection(
      state: CowState,
      payload: { id: string; documentName: string },
    ) {
      state.collabConnections[payload.id] = {
        documentName: payload.documentName,
        status: 'connected',
        hasUnsyncedChanges: false,
      };
    },
    updateCollabConnection(
      state: CowState,
      payload: { id: string; status?: CollabStatus; hasUnsyncedChanges?: boolean },
    ) {
      const entry = state.collabConnections[payload.id];
      if (!entry) {
        return;
      }
      if (payload.status !== undefined) {
        entry.status = payload.status;
      }
      if (payload.hasUnsyncedChanges !== undefined) {
        entry.hasUnsyncedChanges = payload.hasUnsyncedChanges;
      }
    },
    unregisterCollabConnection(state: CowState, id: string) {
      delete state.collabConnections[id];
    },
  },
});

export default store;
